/**
 * Error handling utility for application-wide standardized error management
 */

// Error types to categorize different kinds of errors
export const ERROR_TYPES = {
    API_KEY: 'API_KEY',
    RATE_LIMIT: 'RATE_LIMIT',
    NETWORK: 'NETWORK',
    TIMEOUT: 'TIMEOUT',
    VALIDATION: 'VALIDATION',
    SERVER: 'SERVER',
    UNKNOWN: 'UNKNOWN'
};

/**
 * Parse error messages from different sources and categorize them
 * @param {Error} error - The error object
 * @returns {Object} Processed error with type and message
 */
export const parseError = (error) => {
    const isDebugMode = process.env.REACT_APP_DEBUG_MODE === 'true';
    const originalMessage = error.message || 'An unknown error occurred';
    let errorType = ERROR_TYPES.UNKNOWN;
    let userMessage = 'Something went wrong. Please try again later.';
    let details = isDebugMode ? originalMessage : null;

    // Check if the error is from an API response
    if (error.response) {
        const status = error.response.status;
        const responseData = error.response.data || {};

        // Handle API key related errors
        if (status === 401 || originalMessage.includes('API key') ||
            originalMessage.includes('authentication') ||
            originalMessage.includes('auth')) {
            errorType = ERROR_TYPES.API_KEY;
            userMessage = 'API authentication failed. Please check your API key.';
        }
        // Handle rate limit errors
        else if (status === 429 || originalMessage.includes('rate limit') ||
            originalMessage.includes('quota') || responseData.error?.type === 'insufficient_quota') {
            errorType = ERROR_TYPES.RATE_LIMIT;
            userMessage = 'Rate limit exceeded. Please try again later or check your subscription.';
        }
        // Handle validation errors
        else if (status === 400 || status === 422) {
            errorType = ERROR_TYPES.VALIDATION;
            userMessage = 'The request was invalid. Please check your inputs.';
        }
        // Handle server errors
        else if (status >= 500) {
            errorType = ERROR_TYPES.SERVER;
            userMessage = 'Server error. Please try again later.';
        }

        // Extract more detailed message if available in the response
        if (responseData.error?.message) {
            details = responseData.error.message;
        } else if (responseData.message) {
            details = responseData.message;
        }
    }
    // Handle network errors
    else if (error.request || originalMessage.includes('network') ||
        originalMessage.includes('ECONNREFUSED')) {
        errorType = ERROR_TYPES.NETWORK;
        userMessage = 'Network error. Please check your internet connection.';
    }
    // Handle timeout errors
    else if (originalMessage.includes('timeout')) {
        errorType = ERROR_TYPES.TIMEOUT;
        userMessage = 'Request timed out. Please try again.';
    }

    return {
        type: errorType,
        message: userMessage,
        details: details,
        original: error
    };
};

/**
 * Log errors based on environment
 * @param {Object} parsedError - The parsed error object
 */
export const logError = (parsedError) => {
    const isDebugMode = process.env.REACT_APP_DEBUG_MODE === 'true';

    // Always log the error type and message
    console.error(`[${parsedError.type}] ${parsedError.message}`);

    // In debug mode, log more details
    if (isDebugMode) {
        console.error('Error details:', parsedError.details);
        console.error('Original error:', parsedError.original);
    }
};

/**
 * Main error handler function
 * @param {Error} error - The original error
 * @returns {Object} Standardized error object
 */
export const handleError = (error) => {
    const parsedError = parseError(error);
    logError(parsedError);
    return parsedError;
};

export default {
    handleError,
    parseError,
    logError,
    ERROR_TYPES
};
